// src/hooks/useCircleRealtime.ts
//
// Subscribe to CIRCLE_* realtime events for a workspace and invalidate
// the relevant RTK Query tags so the list, detail, and history views
// refresh without a manual refetch.

import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { realtimeClient } from '../realtime/socketClient';
import {
  RealtimeEventType,
  type RealtimeEventTypeValue,
  type CircleRealtimePayload,
} from '../realtime/events';
import { circlesApi } from '../api/circlesApi';

interface UseCircleRealtimeOptions {
  workspaceId?: string | null;
  /** Circle currently being viewed (invalidates detail cache directly). */
  activeCircleId?: string | null;
  onCircleChanged?: (payload: CircleRealtimePayload, kind: CircleChangeKind) => void;
}

export type CircleChangeKind =
  | 'created'
  | 'updated'
  | 'archived'
  | 'restored'
  | 'deleted'
  | 'member_added'
  | 'member_removed';

const CIRCLE_EVENT_MAP: Array<[RealtimeEventTypeValue, CircleChangeKind]> = [
  [RealtimeEventType.CIRCLE_CREATED, 'created'],
  [RealtimeEventType.CIRCLE_UPDATED, 'updated'],
  [RealtimeEventType.CIRCLE_ARCHIVED, 'archived'],
  [RealtimeEventType.CIRCLE_RESTORED, 'restored'],
  [RealtimeEventType.CIRCLE_DELETED, 'deleted'],
  [RealtimeEventType.CIRCLE_MEMBER_ADDED, 'member_added'],
  [RealtimeEventType.CIRCLE_MEMBER_REMOVED, 'member_removed'],
];

/**
 * Wire CIRCLE_* realtime events into RTK Query cache invalidation.
 * Safe to call from multiple mount points; the underlying socket
 * subscribe is refcounted per topic by realtimeClient.
 */
export function useCircleRealtime({
  workspaceId,
  activeCircleId,
  onCircleChanged,
}: UseCircleRealtimeOptions) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dispatch = useDispatch<any>();

  useEffect(() => {
    if (!workspaceId) return;
    const topic = `workspace:${workspaceId}`;
    realtimeClient.subscribe(topic);

    const invalidate = (payload: CircleRealtimePayload, kind: CircleChangeKind) => {
      const tags: Array<{ type: 'Circle'; id: string }> = [
        { type: 'Circle', id: 'LIST' },
      ];
      if (payload.circleId) {
        tags.push({ type: 'Circle', id: payload.circleId });
        tags.push({ type: 'Circle', id: `${payload.circleId}-history` });
      }
      dispatch(circlesApi.util.invalidateTags(tags));
      onCircleChanged?.(payload, kind);
    };

    const listeners = CIRCLE_EVENT_MAP.map(([eventType, kind]) =>
      realtimeClient.on<CircleRealtimePayload>(
        topic,
        (payload) => invalidate(payload, kind),
        eventType,
      ),
    );

    return () => {
      for (const off of listeners) off();
      realtimeClient.unsubscribe(topic);
    };
  }, [workspaceId, activeCircleId, dispatch, onCircleChanged]);
}

export default useCircleRealtime;
